// Shared client-side address ↔ ZIP cross-check used by both the customer
// signup (`CustomerOnboarding`) and the restaurant signup (`RegisterPage`).
// Mirror of the backend `zip_lookup_service.detect_address_state_mismatch` —
// kept in JS so we can surface the error before the user submits.

export const STATE_NAMES = {
  alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR',
  california: 'CA', colorado: 'CO', connecticut: 'CT', delaware: 'DE',
  florida: 'FL', georgia: 'GA', hawaii: 'HI', idaho: 'ID',
  illinois: 'IL', indiana: 'IN', iowa: 'IA', kansas: 'KS',
  kentucky: 'KY', louisiana: 'LA', maine: 'ME', maryland: 'MD',
  massachusetts: 'MA', michigan: 'MI', minnesota: 'MN', mississippi: 'MS',
  missouri: 'MO', montana: 'MT', nebraska: 'NE', nevada: 'NV',
  'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM',
  'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND',
  ohio: 'OH', oklahoma: 'OK', oregon: 'OR', pennsylvania: 'PA',
  'rhode island': 'RI', 'south carolina': 'SC', 'south dakota': 'SD',
  tennessee: 'TN', texas: 'TX', utah: 'UT', vermont: 'VT',
  virginia: 'VA', washington: 'WA', 'west virginia': 'WV',
  wisconsin: 'WI', wyoming: 'WY',
};
export const STATE_ABBREVS = new Set(Object.values(STATE_NAMES));
export const MAJOR_CITY_STATE = {
  'new york': 'NY', nyc: 'NY', brooklyn: 'NY', queens: 'NY', manhattan: 'NY', bronx: 'NY',
  'los angeles': 'CA', 'san francisco': 'CA', 'san diego': 'CA', oakland: 'CA', sacramento: 'CA',
  chicago: 'IL', houston: 'TX', dallas: 'TX', austin: 'TX', 'san antonio': 'TX',
  phoenix: 'AZ', tucson: 'AZ', philadelphia: 'PA', pittsburgh: 'PA',
  miami: 'FL', orlando: 'FL', tampa: 'FL', jacksonville: 'FL', 'fort lauderdale': 'FL', kissimmee: 'FL',
  boston: 'MA', cambridge: 'MA', worcester: 'MA',
  seattle: 'WA', denver: 'CO', atlanta: 'GA', detroit: 'MI', minneapolis: 'MN',
  'new orleans': 'LA', nashville: 'TN', memphis: 'TN', charlotte: 'NC', raleigh: 'NC',
  indianapolis: 'IN', columbus: 'OH', cleveland: 'OH', cincinnati: 'OH',
  louisville: 'KY', milwaukee: 'WI', 'kansas city': 'MO', 'saint louis': 'MO',
  portland: 'OR', newark: 'NJ', 'jersey city': 'NJ', 'las vegas': 'NV',
  'salt lake city': 'UT', 'oklahoma city': 'OK', tulsa: 'OK', honolulu: 'HI',
  providence: 'RI', hartford: 'CT', baltimore: 'MD', richmond: 'VA',
  'virginia beach': 'VA', albuquerque: 'NM', boise: 'ID', 'des moines': 'IA',
  omaha: 'NE', wichita: 'KS', anchorage: 'AK', 'little rock': 'AR',
  birmingham: 'AL', 'sioux falls': 'SD', fargo: 'ND', burlington: 'VT',
  manchester: 'NH', wilmington: 'DE', cheyenne: 'WY', billings: 'MT',
};

/**
 * Returns `{ message }` describing a mismatch between the typed address and
 * the resolved ZIP state, or `null` if no contradiction is found.
 */
export function detectAddressMismatch(address, expState, expCity) {
  if (!address || !expState) return null;
  const text = ` ${address.toLowerCase().replace(/[^a-z\s,]/g, ' ')} `;
  for (const abbr of STATE_ABBREVS) {
    if (abbr === expState) continue;
    const re = new RegExp(`(?:^|[\\s,])${abbr.toLowerCase()}(?=[\\s,]|$)`);
    if (re.test(text)) {
      return {
        message: `Your ZIP code resolves to ${expState}, but your address mentions ${abbr}. Please make sure the address and ZIP code are in the same state.`,
      };
    }
  }
  for (const [name, abbr] of Object.entries(STATE_NAMES)) {
    if (abbr === expState) continue;
    const re = new RegExp(`(?:^|[\\s,])${name.replace(/ /g, '\\s')}(?:[\\s,]|$)`);
    if (re.test(text)) {
      return {
        message: `Your ZIP code resolves to ${expState}, but your address mentions ${name.replace(/\b\w/g, (c) => c.toUpperCase())}. Please make sure the address and ZIP code are in the same state.`,
      };
    }
  }
  for (const [city, abbr] of Object.entries(MAJOR_CITY_STATE)) {
    if (abbr === expState) continue;
    if (expCity && city === expCity) continue;
    const re = new RegExp(`(?:^|[\\s,])${city.replace(/ /g, '\\s')}(?:[\\s,]|$)`);
    if (re.test(text)) {
      const cityTitle = city.replace(/\b\w/g, (c) => c.toUpperCase());
      return {
        message: `Your ZIP code resolves to ${expState}, but the address mentions ${cityTitle} which is in ${abbr}. Please correct the address or the ZIP code.`,
      };
    }
  }
  return null;
}

/**
 * Format a partial phone-number string as US-style "(XXX) XXX-XXXX" while the
 * user is typing. Strips non-digits, caps at 10 digits, and progressively
 * builds the mask so deleting characters always works.
 */
export function formatUSPhone(value) {
  if (!value) return '';
  const digits = String(value).replace(/\D/g, '').slice(0, 10);
  if (digits.length < 4) return digits;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}
